import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { getStoredUser } from "../utilities/authentication";

const selectLoginUser = (state) => state?.login?.user ?? null;

const collectPageNames = (pages, names = []) => {
  if (!Array.isArray(pages)) {
    return names;
  }

  for (const page of pages) {
    if (typeof page?.name === "string" && page.name.length > 0) {
      names.push(page.name);
    }

    if (Array.isArray(page?.children) && page.children.length > 0) {
      collectPageNames(page.children, names);
    }
  }

  return names;
};

const findPageByName = (pages, pageName) => {
  if (!Array.isArray(pages)) {
    return null;
  }

  for (const page of pages) {
    if (page?.name === pageName) {
      return page;
    }

    if (Array.isArray(page?.children) && page.children.length > 0) {
      const nested = findPageByName(page.children, pageName);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

export const  usePageAccess = () => {
  const stateUser = useSelector(selectLoginUser);
  const user = useMemo(() => stateUser ?? getStoredUser() ?? {}, [stateUser]);

  const pages = useMemo(() => {
    const candidate = user?.pages;
    return Array.isArray(candidate) ? candidate : [];
  }, [user]);

  const menu = useMemo(() => {
    const candidate = user?.menu;
    return Array.isArray(candidate) ? candidate : [];
  }, [user]);

  const pageSet = useMemo(() => new Set(collectPageNames(pages)), [pages]);

  const hasPage = useCallback(
    (pageName) => (typeof pageName === "string" ? pageSet.has(pageName) : false),
    [pageSet]
  );

  const hasAnyPage = useCallback(
    (pageNames) =>
      Array.isArray(pageNames) && pageNames.some((pageName) => pageSet.has(pageName)),
    [pageSet]
  );

  const hasAllPages = useCallback(
    (pageNames) =>
      Array.isArray(pageNames) && pageNames.every((pageName) => pageSet.has(pageName)),
    [pageSet]
  );

  const getPage = useCallback(
    (pageName) => findPageByName(pages, pageName),
    [pages]
  );

  const permissionSet = useMemo(() => {
    const names = (user?.permissions ?? [])
      .map((permission) => (typeof permission === "string" ? permission : permission?.name))
      .filter((name) => typeof name === "string" && name.length > 0);

    return new Set(names);
  }, [user]);

  const hasPermission = useCallback(
    (permissionName) => (typeof permissionName === "string" ? permissionSet.has(permissionName) : false),
    [permissionSet]
  );

  return {
    user,
    pages,
    menu,
    hasPage,
    hasPermission,
    hasAnyPage,
    hasAllPages,
    getPage,
  };
};

export const useCrudAccess = (resource) => {
  const normalized = typeof resource === "string" ? resource.trim() : "";
  const { hasPage } = usePageAccess();

  return useMemo(
    () => ({
      canList: normalized ? hasPage(`${normalized}.list`) : false,
      canCreate: normalized ? hasPage(`${normalized}.create`) : false,
      canEdit: normalized ? hasPage(`${normalized}.edit`) : false,
      canDelete: normalized ? hasPage(`${normalized}.delete`) : false,
      canView: normalized ? hasPage(`${normalized}.view`) : false,
    }),
    [hasPage, normalized]
  );
};
